/**
 * Time-phased planned units (project controls): spread each activity's planned units
 * evenly over calendar days from start→finish, then aggregate by date.
 * MVP: calendar-day spread only (honest in chart copy vs P6 calendars).
 */

import type { ScheduleActivity } from '@/types';

export type TimePhasedDateBasis = 'baseline' | 'current';

export interface TimePhasedUnitsOptions {
  /** baseline = target start/finish; current = early (CPM) start/finish with fallbacks */
  dateBasis: TimePhasedDateBasis;
  /**
   * When false (default), TT_Mile / TT_FinMile and milestone display types are excluded (single-day spikes).
   * Enable when milestones carry planned workload you want in the histogram.
   */
  includeMilestones?: boolean;
}

export interface TimePhasedUnitsPoint {
  date: string;
  unitsPerDay: number;
  cumulativeUnits: number;
}

/** One activity’s contribution on a single calendar date (histogram diagnostic). */
export interface TimePhasedContribution {
  activityId: string;
  activityName: string;
  activityType: string;
  startDateUsed: string;
  finishDateUsed: string;
  totalPlannedUnits: number;
  dailyContribution: number;
  durationDays: number;
  wbs: string;
}

export interface TimePhasedWorkloadStats {
  includedActivityCount: number;
  excludedMilestoneCount: number;
  excludedNoUnitsCount: number;
  excludedInvalidDateCount: number;
  /** LOE / WBS summary / unsupported task shapes */
  excludedNonWorkTypeCount: number;
  /** Included workload with inclusive span = 1 calendar day */
  singleDayIncludedCount: number;
  /** Activities using Mon–Fri or project-default fallback (server path) */
  fallbackCalendarCount?: number;
  /** Calendars with unparsed clndr_data (server path) */
  unparsedCalendarCount?: number;
  /** True when histogram uses P6 working-day spread from server */
  usedWorkingDaySpread?: boolean;
}

export interface TimePhasedUnitsResult {
  series: TimePhasedUnitsPoint[];
  totalPlannedUnits: number;
  stats: TimePhasedWorkloadStats;
  /** YYYY-MM-DD → contributors that day (unsorted); sort UI-side by dailyContribution */
  contributorsByDate: Map<string, TimePhasedContribution[]>;
}

function toYmd(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (s.length >= 10) {
    const head = s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  }
  return null;
}

function parseYmd(ymd: string): Date {
  const [y, m, d] = ymd.split('-').map((x) => Number(x));
  return new Date(Date.UTC(y, m - 1, d));
}

function formatYmd(d: Date): string {
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function addUtcDay(d: Date): Date {
  const n = new Date(d.getTime());
  n.setUTCDate(n.getUTCDate() + 1);
  return n;
}

function inclusiveDayCount(startYmd: string, endYmd: string): number {
  const a = parseYmd(startYmd);
  const b = parseYmd(endYmd);
  if (b < a) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

function enumerateInclusiveDays(startYmd: string, endYmd: string): string[] {
  const out: string[] = [];
  let cur = parseYmd(startYmd);
  const end = parseYmd(endYmd);
  while (cur <= end) {
    out.push(formatYmd(cur));
    cur = addUtcDay(cur);
  }
  return out;
}

function getPlannedUnits(a: ScheduleActivity): number {
  const raw = a as Record<string, unknown>;
  const v = raw.target_work_qty;
  const n = typeof v === 'number' ? v : Number(String(v ?? '').replace(/,/g, ''));
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n;
}

function activityRowType(a: ScheduleActivity): 'nonwork' | 'milestone' | 'task' {
  const raw = a as Record<string, unknown>;
  const taskType = String(raw.task_type ?? '');
  if (taskType === 'TT_LOE' || taskType === 'TT_WBS') return 'nonwork';

  if (taskType === 'TT_Mile' || taskType === 'TT_FinMile') return 'milestone';
  const type = String(a.type ?? '');
  if (/milestone/i.test(type)) return 'milestone';

  if (taskType === 'TT_Task' || type === 'Task' || type === 'TT_Task') return 'task';

  if (!taskType && type && type !== '') {
    if (/WBS|LOE/i.test(type)) return 'nonwork';
    return 'task';
  }
  return 'nonwork';
}

function activityTaskTypeLabel(a: ScheduleActivity): string {
  const raw = a as Record<string, unknown>;
  const tt = raw.task_type;
  if (tt != null && String(tt).trim() !== '') return String(tt);
  return String(a.type ?? 'Unknown');
}

function activityId(a: ScheduleActivity): string {
  const raw = a as Record<string, unknown>;
  return String(raw.task_code ?? raw.task_id ?? a.id ?? a.activity_id ?? '—').trim() || '—';
}

function wbsLabel(a: ScheduleActivity): string {
  const raw = a as Record<string, unknown>;
  const short = raw.wbs_short_name ?? raw.wbs_code;
  const name = raw.wbs_name ?? a.wbs_name;
  const wid = raw.wbs_id ?? a.wbs_id;
  const parts = [short, name, wid].filter((x) => x != null && String(x).trim() !== '').map(String);
  if (parts.length === 0) return '—';
  return [...new Set(parts)].join(' · ');
}

function getStartFinishForBasis(a: ScheduleActivity, basis: TimePhasedDateBasis): { start: string | null; finish: string | null } {
  const raw = a as Record<string, unknown>;
  if (basis === 'baseline') {
    const start = toYmd(raw.target_start_date ?? a.target_start_date);
    const finish = toYmd(raw.target_end_date ?? raw.target_finish_date ?? a.target_finish_date);
    return { start, finish };
  }
  const start = toYmd(
    raw.early_start_date ?? a.early_start_date ?? a.start_date ?? raw.planned_start_date ?? a.planned_start_date
  );
  const finish = toYmd(
    raw.early_end_date ?? a.early_end_date ?? a.end_date ?? raw.planned_finish_date ?? a.planned_finish_date
  );
  return { start, finish };
}

function appendContributor(
  byDate: Map<string, TimePhasedContribution[]>,
  ymd: string,
  row: TimePhasedContribution
): void {
  const list = byDate.get(ymd) ?? [];
  list.push(row);
  byDate.set(ymd, list);
}

/**
 * Build daily planned units (histogram) and cumulative planned units (S-curve) from activities.
 */
export function buildTimePhasedUnitsSeries(
  activities: ScheduleActivity[],
  options: TimePhasedUnitsOptions
): TimePhasedUnitsResult {
  const includeMilestones = options.includeMilestones === true;

  const stats: TimePhasedWorkloadStats = {
    includedActivityCount: 0,
    excludedMilestoneCount: 0,
    excludedNoUnitsCount: 0,
    excludedInvalidDateCount: 0,
    excludedNonWorkTypeCount: 0,
    singleDayIncludedCount: 0,
    usedWorkingDaySpread: false,
  };

  const daily = new Map<string, number>();
  const contributorsByDate = new Map<string, TimePhasedContribution[]>();
  let totalPlannedUnits = 0;

  for (const a of activities) {
    const rowKind = activityRowType(a);

    if (rowKind === 'nonwork') {
      stats.excludedNonWorkTypeCount += 1;
      continue;
    }

    const isMil = rowKind === 'milestone';

    if (isMil && !includeMilestones) {
      stats.excludedMilestoneCount += 1;
      continue;
    }

    const units = getPlannedUnits(a);
    if (units <= 0) {
      stats.excludedNoUnitsCount += 1;
      continue;
    }

    let { start, finish } = getStartFinishForBasis(a, options.dateBasis);
    if (!start && !finish) {
      stats.excludedInvalidDateCount += 1;
      continue;
    }
    if (!start) start = finish!;
    if (!finish) finish = start;

    totalPlannedUnits += units;

    stats.includedActivityCount += 1;

    const ty = activityTaskTypeLabel(a);
    const id = activityId(a);
    const nm = String(a.name ?? '—');

    let activeDays = inclusiveDayCount(start, finish);
    if (activeDays <= 0) {
      stats.singleDayIncludedCount += 1;
      const perDayAll = units;
      const prev = daily.get(start) ?? 0;
      daily.set(start, prev + perDayAll);
      appendContributor(contributorsByDate, start, {
        activityId: id,
        activityName: nm,
        activityType: ty,
        startDateUsed: start,
        finishDateUsed: finish,
        totalPlannedUnits: units,
        dailyContribution: perDayAll,
        durationDays: 1,
        wbs: wbsLabel(a),
      });
      continue;
    }

    if (activeDays === 1) {
      stats.singleDayIncludedCount += 1;
    }

    const perDay = units / activeDays;

    const rowBase: Omit<TimePhasedContribution, 'dailyContribution'> = {
      activityId: id,
      activityName: nm,
      activityType: ty,
      startDateUsed: start,
      finishDateUsed: finish,
      totalPlannedUnits: units,
      durationDays: activeDays,
      wbs: wbsLabel(a),
    };

    for (const ymd of enumerateInclusiveDays(start, finish)) {
      daily.set(ymd, (daily.get(ymd) ?? 0) + perDay);
      appendContributor(contributorsByDate, ymd, {
        ...rowBase,
        dailyContribution: perDay,
      });
    }
  }

  const dates = [...daily.keys()].sort();
  let cumRaw = 0;
  const series: TimePhasedUnitsPoint[] = dates.map((date) => {
    const rawDay = daily.get(date) ?? 0;
    cumRaw += rawDay;
    return {
      date,
      unitsPerDay: Number(rawDay.toFixed(2)),
      cumulativeUnits: Number(cumRaw.toFixed(2)),
    };
  });

  const roundedTotal = Number(totalPlannedUnits.toFixed(2));
  if (series.length > 0) {
    const last = series[series.length - 1];
    if (Math.abs(last.cumulativeUnits - roundedTotal) <= 0.02) {
      last.cumulativeUnits = roundedTotal;
    }
  }

  return { series, totalPlannedUnits: roundedTotal, stats, contributorsByDate };
}
